import type { Questionnaire } from '@medplum/fhirtypes'
import type {
  ExtendedQuestionnaireResponse,
  QuestionnaireWithResponse,
} from '#src/models/questionnaireResponse.ts'
import {
  DietQuestionnaire,
  EmergencyContactAndDecisionsQuestionnaire,
  PatientEvaluationQuestionnaire,
  PatientPhysicalFunctioningQuestionnaire,
  ServiceAllocationQuestionnaire,
} from '#src/models/questionnaires/definitions/patient/index.ts'

export const CARE_PLAN_DEMOGRAPHIC_QUESTIONNAIRES: readonly Questionnaire[] = [
  EmergencyContactAndDecisionsQuestionnaire,
  PatientEvaluationQuestionnaire,
  PatientPhysicalFunctioningQuestionnaire,
  DietQuestionnaire,
  ServiceAllocationQuestionnaire,
]

/* The subset of the demographic questionnaires the AI assistant may read and
 * propose answers for. Emergency Contact and Service Allocation are excluded:
 * they hold structured contact and scheduling data the assistant does not
 * draft. Shared by the read tool and the client staging handler so the URLs the
 * model is told it may propose for cannot drift from the ones accepted. */
export const AI_PROPOSABLE_CARE_PLAN_QUESTIONNAIRES: readonly Questionnaire[] =
  [
    PatientEvaluationQuestionnaire,
    PatientPhysicalFunctioningQuestionnaire,
    DietQuestionnaire,
  ]

export const CARE_PLAN_QUESTIONNAIRE_NODE_TYPE = 'care-plan-questionnaire'

/* Matches one embedded questionnaire node, capturing its questionnaire URL.
 * Both attributes are required, and the lookaheads make their order in the tag
 * irrelevant — TipTap re-serializes stored HTML with attributes in a different
 * order than it wrote them. Requiring the URL is what keeps splitCarePlanDescription
 * aligned: a node the URL scan skipped would otherwise still split the narrative,
 * shifting every questionnaire after it into the wrong position. */
const QUESTIONNAIRE_NODE =
  /<div\b(?=[^>]*\bdata-type=["']care-plan-questionnaire["'])(?=[^>]*\bdata-questionnaire-url=["']([^"']+)["'])[^>]*>(?:<\/div>)?/i

export interface CarePlanDescriptionParts {
  // One more narrative run than URLs: the HTML before each node, plus whatever
  // follows the last one. A run is empty when two nodes sit next to each other.
  narratives: string[]
  questionnaireUrls: string[]
}

/* Splits a care plan Description into its narrative runs and the questionnaire
 * nodes embedded between them, in document order. Callers render each
 * questionnaire in exactly one place: one the Description already renders must
 * not be repeated in the Participant Summary panel or the PDF's Patient Summary.
 * Reading the stored Description rather than a feature flag is what keeps plans
 * predating the move — including signed snapshots — rendering correctly. */
export function splitCarePlanDescription(
  description: string | null | undefined
): CarePlanDescriptionParts {
  if (!description) {
    return { narratives: [''], questionnaireUrls: [] }
  }
  // String.split interleaves the capture group, so one pass yields both halves
  // already paired: narrative, url, narrative, url, ..., narrative.
  const parts = description.split(QUESTIONNAIRE_NODE)
  return {
    narratives: parts.filter((_, index) => index % 2 === 0),
    questionnaireUrls: parts.filter((_, index) => index % 2 === 1),
  }
}

export function parseDescriptionQuestionnaireUrls(
  description: string | null | undefined
): string[] {
  return splitCarePlanDescription(description).questionnaireUrls
}

/* The demographic questionnaires a care plan's own page renders: the ones its
 * Description embeds, and — on a plan that still shows its Participant Summary
 * — every other one, which that panel renders in their place. A questionnaire
 * left out has no surface on the page at all, so an AI suggestion staged for it
 * would be counted and shown nowhere. Shared by the server, which offers the
 * model only these, and the client staging handler, which refuses the rest. */
export function carePlanPageQuestionnaireUrls(carePlan: {
  description: string
  noParticipantSummary: boolean
}): string[] {
  const embedded = parseDescriptionQuestionnaireUrls(carePlan.description)
  if (carePlan.noParticipantSummary) {
    return embedded
  }
  return [
    ...embedded,
    ...CARE_PLAN_DEMOGRAPHIC_QUESTIONNAIRES.flatMap((questionnaire) =>
      questionnaire.url && !embedded.includes(questionnaire.url)
        ? [questionnaire.url]
        : []
    ),
  ]
}

export function mapDemographicsQuestionnaireResponses(
  responses: (ExtendedQuestionnaireResponse | null | undefined)[]
): QuestionnaireWithResponse[] {
  return CARE_PLAN_DEMOGRAPHIC_QUESTIONNAIRES.map((questionnaire) => {
    const matchingResponse = responses.find(
      (response) => response?.questionnaireUrl === questionnaire.url
    )
    return {
      questionnaire,
      questionnaireResponse: matchingResponse ?? null,
    }
  })
}
